import {configureStore} from "@reduxjs/toolkit";
import i18next from "i18next";
import _ from "lodash";
import {initReactI18next} from "react-i18next";
import {
  getAppConfigUrl,
  getHubStartupInfoUrl,
  getInitConfigUrl,
  getMapLayersConfigUrl,
  getModuleConfigsUrl
} from "./constants/apiEndpoints";
import {init as initAction} from "./state/rootActions";
import rootReducer from "./state/rootReducer";
import middlewareList from "./middlewares/middlewareList";
import {ensureLeadingSlashOnly, ensureTrailingSlash} from "./utils/urls";
import {isValidIntegerInInclusiveRange} from "./utils/validator";
import availableThemes from "./theme-config/availableThemes.json";

const getThemeAppConfig = (appConfig, themeConfig) => {
  const newAppConfig = {};

  Object.keys(themeConfig.appConfig).forEach(key => {
    if (themeConfig.configsToMerge.includes(key)) {
      newAppConfig[key] = {
        ...themeConfig.appConfig[key],
        ...(appConfig?.[key] || {})
      };
    } else if (
      typeof themeConfig.appConfig[key] === "object" &&
      !Array.isArray(themeConfig.appConfig[key]) &&
      themeConfig.appConfig[key] !== null
    ) {
      newAppConfig[key] = {};
      Object.keys(themeConfig.appConfig[key]).forEach(subKey => {
        if (themeConfig.configsToMerge.includes(subKey)) {
          newAppConfig[key][subKey] = {
            ...themeConfig.appConfig[key][subKey],
            ...(appConfig?.[key]?.[subKey] || {})
          };
        } else {
          newAppConfig[key][subKey] =
            appConfig?.[key]?.[subKey] !== undefined ? appConfig[key][subKey] : themeConfig.appConfig[key][subKey];
        }
      });
    } else {
      newAppConfig[key] = appConfig?.[key] !== undefined ? appConfig[key] : themeConfig.appConfig[key];
    }
  });

  return newAppConfig;
};

const $ = window.jQuery;

const getRandomParam = () => "?random=" + Math.floor(Math.random() * 16777215).toString(16);

const configFiles = [
  {
    url: getAppConfigUrl(),
    param: "appConfig"
  },
  {
    url: getMapLayersConfigUrl(),
    param: "mapLayersConfig"
  },
  {
    url: getModuleConfigsUrl(),
    param: "moduleConfigs"
  }
];

const showError = text => {
  $(".preloader__spinner").hide();

  if (text) {
    $(".preloader__error-text").text(text);
  }

  $(".preloader__error").show();
};

// function to convert dot notation to a nested object
const expandDotNotation = obj => {
  const result = {};

  for (const key in obj) {
    const keys = key.split("."); // split of keys for the '.'
    keys.reduce((acc, curr, index) => {
      if (index === keys.length - 1) {
        acc[curr] = obj[key]; // if we are at the last key, we assign the value
      } else {
        acc[curr] = acc[curr] || {}; // if it is not the last key, we create an empty object if it does not exist
      }
      return acc[curr]; // returns the current level of the object
    }, result);
  }

  return result;
};

const init = async cb => {
  try {
    const configs = {};
    const params = new URLSearchParams(window.location.search);

    // check if safe mode is active
    const safeMode = params.get("safemode");

    // fetch config.json
    const initResponse = await fetch(`/${getInitConfigUrl()}${getRandomParam()}`);
    const initResponseParsed = await initResponse.json();

    const {clientBasePath: origClientBasePath, baseURL: origBaseURL, externalServices} = initResponseParsed;

    // handling config urls
    const clientBasePath = ensureLeadingSlashOnly(origClientBasePath);
    const baseURL = ensureTrailingSlash(origBaseURL);
    configs.externalServices = _.mapValues(externalServices, ensureTrailingSlash);

    // fetch config files
    for (const config of configFiles) {
      const response = await fetch(`${clientBasePath}/${config.url}${getRandomParam()}`);
      const responseParsed =
        response.ok && response.headers.get("content-type").includes("application/json") ? await response.json() : null;
      configs[config.param] = responseParsed;
    }

    // If Matomo tracking system is active load the script
    if (configs?.appConfig?.matomo?.enabled) {
      // if tag manager url is provided, it will be used instead of the tracker
      if (configs.appConfig.matomo.tagManager?.url) {
        const _mtm = (window._mtm = window._mtm || []);
        _mtm.push({"mtm.startTime": new Date().getTime(), event: "mtm.Start"});
        (function () {
          const d = document,
            g = d.createElement("script"),
            s = d.getElementsByTagName("script")[0];
          g.async = true;
          g.src = configs.appConfig.matomo.tagManager.url;
          s.parentNode.insertBefore(g, s);
        })();
        // if no tag manager url is provided, the tracker will be used if the script and tracker urls are provided
      } else if (
        configs.appConfig.matomo.tracker?.scriptUrl &&
        configs.appConfig.matomo.tracker?.trackerUrl &&
        configs.appConfig.matomo.tracker?.siteId
      ) {
        var _paq = (window._paq = window._paq || []);
        /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
        _paq.push(["trackPageView"]);
        _paq.push(["enableLinkTracking"]);
        (function () {
          _paq.push(["setTrackerUrl", configs.appConfig.matomo.tracker.trackerUrl]);
          _paq.push(["setSiteId", configs.appConfig.matomo.tracker.siteId]);
          var d = document,
            g = d.createElement("script"),
            s = d.getElementsByTagName("script")[0];
          g.async = true;
          g.src = configs.appConfig.matomo.tracker.scriptUrl;
          s.parentNode.insertBefore(g, s);
        })();
      }
    }

    // fetch startup info
    const startupInfoResponse = await fetch(`${baseURL}${getHubStartupInfoUrl()}`);
    const startupInfoResponseParsed = await startupInfoResponse.json();

    // handling app license
    configs.license = {
      ...(startupInfoResponseParsed?.license || {}),
      features: Object.keys(startupInfoResponseParsed?.license?.features ?? {}).reduce((acc, key) => {
        acc[key] = startupInfoResponseParsed.license.features[key] === "True";
        return acc;
      }, {})
    };

    // handling hub custom style
    if (!safeMode) {
      const hubCustomStyle = (startupInfoResponseParsed?.style || "").trim();
      if (hubCustomStyle.length > 0) {
        const styleElement = document.createElement("style");
        styleElement.id = "hub-custom-style";
        styleElement.textContent = startupInfoResponseParsed.style;
        styleElement.onerror = e => {
          console.error("Error on application CSS", e);
          styleElement.remove();
        };
        document.head.appendChild(styleElement);
      }
    } else {
      console.warn("Safe mode active: custom styles not loaded");
    }

    // available translations
    const resources = {};

    // fetch translations for supported languages
    for (const lang of startupInfoResponseParsed?.languages ?? []) {
      const response = await fetch(`${clientBasePath}/i18n/${lang}.json${getRandomParam()}`);
      const responseParsed =
        response.ok && response.headers.get("content-type").includes("application/json") ? await response.json() : null;
      if (responseParsed) {
        resources[lang] = {translation: responseParsed};
      }
    }

    // filtering supported languages based on available translations
    const supportedLanguages = (startupInfoResponseParsed?.languages ?? []).filter(
      lang => resources[lang] !== undefined && resources[lang] !== null
    );

    if (supportedLanguages.length === 0) {
      // no available translations
      console.error(
        "Unable to find translation file for at least one configured language. Please check app configuration and translations file."
      );
      showError("Error initializing application.");
      return;
    }

    // Get custom translations for supported languages
    if (!safeMode) {
      if (startupInfoResponseParsed?.translations) {
        try {
          const translations = JSON.parse(startupInfoResponseParsed.translations);
          for (const lang of supportedLanguages) {
            const customTranslation = translations[lang] ? JSON.parse(translations[lang]) : {};
            const translationParsedAndExpanded = expandDotNotation(_.cloneDeep(customTranslation));
            _.merge(resources[lang].translation, translationParsedAndExpanded);
          }
        } catch (_error) {
          console.warn("Unable to load custom translations. Please check the app configuration.");
        }
      }
    } else {
      console.warn("Safe mode active: custom translations not loaded");
    }

    // handling app languages
    const defaultLanguage = supportedLanguages[0];

    // init i18next
    await i18next.use(initReactI18next).init({
      lng: defaultLanguage,
      resources,
      returnEmptyString: false,
      interpolation: {
        escapeValue: false
      },
      compatibilityJSON: "v3",
      missingInterpolationHandler: () => ""
    });

    // enabled modules
    const modules = (configs?.moduleConfigs?.modules || []).filter(({enabled}) => enabled);

    // handling module reducers
    const moduleReducerMap = {};
    for (const module of modules) {
      await import(`./modules/${module.id}/state/reducer.ts`)
        .then(response => (moduleReducerMap[module.id] = response.default))
        .catch(async () => {
          await import(`./modules/${module.id}/state/reducer.js`)
            .then(response => (moduleReducerMap[module.id] = response.default))
            .catch(() => null);
        });
    }

    // handling module middlewares
    const moduleMiddlewareList = [];
    for (const module of modules) {
      await import(`./modules/${module.id}/state/middleware.ts`)
        .then(response => moduleMiddlewareList.push(response.default))
        .catch(async () => {
          await import(`./modules/${module.id}/state/middleware.js`)
            .then(response => moduleMiddlewareList.push(response.default))
            .catch(() => null);
        });
    }

    // create Redux store
    const store = configureStore({
      reducer: {
        ...moduleReducerMap,
        ...rootReducer
      },
      middleware: () => [...middlewareList, ...moduleMiddlewareList]
    });

    // fetch module config files
    const moduleConfigList = await Promise.all(
      modules.map(async module => {
        try {
          return await import(`./modules/${module.id}/config.js`);
        } catch (error) {
          console.warn(`The module ${module.id} does not exist`);
          return null;
        }
      })
    );

    // handling module configs
    const moduleConfigFiles = [];
    modules.forEach(({id, config}, key) => {
      moduleConfigFiles.push({
        id: id,
        config: config,
        ...moduleConfigList[key]
      });
    });
    configs.moduleConfigFiles = moduleConfigFiles;

    // handling detail level from url
    let detailLevelParam = params.get("t") ? decodeURIComponent(params.get("t")) : undefined;
    if (isValidIntegerInInclusiveRange(detailLevelParam)) {
      detailLevelParam = parseInt(detailLevelParam, 10);
    } else {
      detailLevelParam = null;
    }
    configs.detailLevel = detailLevelParam;

    // handling theme config
    const themes = configs.appConfig?.themes
      ? configs.appConfig.themes
          .filter(({id}) => availableThemes.includes(id))
          .map(({id, label}) => ({id: id, label: label ?? id}))
      : availableThemes.map(theme => ({id: theme, label: theme}));

    const hubTheme = themes.find(({id}) => id === startupInfoResponseParsed.theme);
    const theme = hubTheme ? hubTheme : themes[0];
    const themeConfig = (await import(`./theme-config/${theme.id}/config.json`)).default;

    configs.themes = themes;
    configs.themeConfig = {...themeConfig, appConfig: undefined};
    configs.appConfig = getThemeAppConfig(configs.appConfig, themeConfig);

    // handling PWA manifest
    if ((configs.appConfig.pwaManifestPath || "").length > 0) {
      document.getElementById("manifest").href = configs.appConfig.pwaManifestPath;
    }

    // dispatch init action to start the application
    store.dispatch(initAction(clientBasePath, baseURL, supportedLanguages, defaultLanguage, configs));
    cb(store);
  } catch (error) {
    showError();
  }
};

export default init;
